import React from 'react';
import { CellState, Position } from '../types/game';
import { cn } from '../lib/utils';

interface ReversiBoardProps {
  board: CellState[][];
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  validMoves?: Position[];
  lastMove?: Position;
  lastFlippedPieces?: Position[];
  blackScore?: number;
  whiteScore?: number;
  currentPlayer?: 'black' | 'white';
  enlarged?: boolean;
}

const ReversiBoard: React.FC<ReversiBoardProps> = ({
  board,
  onCellClick,
  disabled = false,
  validMoves = [],
  lastMove,
  lastFlippedPieces = [],
  blackScore = 0,
  whiteScore = 0,
  currentPlayer = 'black',
  enlarged = false
}) => {
  const isValidMove = (row: number, col: number): boolean => {
    return validMoves.some(pos => pos.row === row && pos.col === col);
  };

  // 处理点击事件，包括点击已有棋子的情况
  const handleCellClick = (row: number, col: number) => {
    if (disabled) return;

    const cell = board[row][col];

    // 如果点击的是有效移动位置（空白格子）
    if (!cell && isValidMove(row, col)) {
      onCellClick({ row, col });
      return;
    }

    // 如果点击的是已有棋子，提供反馈
    if (cell) {
      const piece = cell === 'black' ? '黑棋' : '白棋';
      console.log(`点击了${piece}，位置(${row}, ${col})`);

      // 可以在这里添加更多的反馈逻辑，比如显示棋子信息
      // 暂时不阻止点击，让用户可以看到控制台日志
    }
  };

  const isLastMove = (row: number, col: number): boolean => {
    return lastMove ? lastMove.row === row && lastMove.col === col : false;
  };

  const isLastFlipped = (row: number, col: number): boolean => {
    return lastFlippedPieces.some(pos => pos.row === row && pos.col === col);
  };

  const getPieceAnimation = (row: number, col: number): string => {
    if (isLastMove(row, col)) {
      return 'animate-pulse ring-4 ring-blue-400 ring-opacity-75';
    }
    if (isLastFlipped(row, col)) {
      return 'animate-bounce';
    }
    return '';
  };

  // 计算位置权重（用于AI评估和显示提示）
  const getPositionWeight = (row: number, col: number): number => {
    const weights = [
      [100, -20, 10, 5, 5, 10, -20, 100],
      [-20, -50, -2, -2, -2, -2, -50, -20],
      [10, -2, -1, -1, -1, -1, -2, 10],
      [5, -2, -1, -1, -1, -1, -2, 5],
      [5, -2, -1, -1, -1, -1, -2, 5],
      [10, -2, -1, -1, -1, -1, -2, 10],
      [-20, -50, -2, -2, -2, -2, -50, -20],
      [100, -20, 10, 5, 5, 10, -20, 100]
    ];
    return weights[row][col];
  };

  const getCellStyle = (row: number, col: number): string => {
    const baseStyle = 'relative border border-gray-600 cursor-pointer transition-all duration-300 bg-green-800 hover:bg-green-700';
    const isValid = isValidMove(row, col);
    const isCorner = (row === 0 || row === 7) && (col === 0 || col === 7);
    const isEdge = (row === 0 || row === 7 || col === 0 || col === 7) && !isCorner;

    let style = baseStyle;

    if (isCorner) {
      style += ' bg-green-900'; // 角落位置更深色
    } else if (isEdge) {
      style += ' bg-green-750'; // 边缘位置稍深
    }
    /*
    if (isValid && !disabled) {
      style += ' ring-1 ring-yellow-400 ring-opacity-60 hover:ring-yellow-300';
    }
    */
    return style;
  };

const getCellSize = (): string => {
  return enlarged ? 'w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28' : 'w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16';
};

const getPieceSize = (): string => {
  return enlarged ? 'w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24' : 'w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14';
};

const getMarkerSize = (): string => {
  return enlarged ? 'w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10' : 'w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6';
};

  return (
    <div className="flex flex-col items-center space-y-6">
      {/* 分数显示 */}
      <div className="lg:hidden flex items-center justify-center space-x-8 bg-white rounded-lg shadow-lg px-6 py-4">
        <div className={`flex items-center space-x-3 ${currentPlayer === 'black' ? 'scale-110 transition-transform' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-black shadow-lg"></div>
          <div className="text-xl font-bold text-gray-800">{blackScore}</div>
        </div>

        <div className="text-gray-400 text-lg">VS</div>

        <div className={`flex items-center space-x-3 ${currentPlayer === 'white' ? 'scale-110 transition-transform' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-300 shadow-lg"></div>
          <div className="text-xl font-bold text-gray-800">{whiteScore}</div>
        </div>
      </div>
      
      {/* 棋盘 */}
      <div className="relative bg-green-700 p-0 md:p-4 rounded-xl shadow-2xl">
        <div className="grid grid-cols-8 gap-0 border-2 border-gray-800">
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  getCellSize(),
                  getCellStyle(rowIndex, colIndex),
                  {
                    "cursor-not-allowed opacity-75": disabled,
                  }
                )}
                onClick={() => handleCellClick(rowIndex, colIndex)}
              >
                {/* 有效移动提示 */}
                {isValidMove(rowIndex, colIndex) && !disabled && !cell && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={cn(
                      "rounded-full opacity-60 animate-pulse",
                      currentPlayer === 'black' ? 'bg-black' : 'bg-white border border-gray-300',
                      getMarkerSize()
                    )}></div>
                  </div>
                )}

                {/* 棋子 */}
                {cell && (
                  <div className="absolute inset-0 flex items-center justify-center p-1">
                    <div
                      className={cn(
                        "w-full h-full rounded-full shadow-xl transition-all duration-500",
                        {
                          "bg-black border-2 border-gray-700": cell === 'black',
                          "bg-white border-2 border-gray-300": cell === 'white',
                        },
                        getPieceAnimation(rowIndex, colIndex)
                      )}
                    >
                      {/* 棋子光泽效果 */}
                      {!enlarged && (
                        <div className="absolute top-3 left-3 md:top-4 md:left-4 md:w-3 md:h-3 bg-white rounded-full opacity-25"></div>
                      )}
                      
                    </div>
                  </div>
                )}

                {/* 坐标标记 */}
                {colIndex === 0 && (
                  <div className={`hidden lg:flex absolute -left-3 top-1/2 transform -translate-y-1/2 text-green-600 font-bold ${enlarged ? 'text-sm sm:text-base' : 'text-xs'}`}>
                    {8 - rowIndex}
                  </div>
                )}
                {rowIndex === 7 && (
                  <div className={`hidden lg:flex absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-green-600 font-bold ${enlarged ? 'text-sm sm:text-base' : 'text-xs'}`}>
                    {String.fromCharCode(65 + colIndex)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 游戏提示 */}
      {validMoves.length === 0 && !disabled && (
        <div className="text-orange-600 font-medium bg-orange-50 px-4 py-2 rounded-lg">
          无有效移动，自动跳过回合
        </div>
      )}
    </div>
  );
};

export default ReversiBoard;